import { useId, useState, type ReactNode } from 'react'
import { Icon } from './icons/Icon'

export interface CollapsibleSectionProps {
  title: string
  children: ReactNode
  /** Defaults to expanded. */
  defaultOpen?: boolean
}

/**
 * A small, titled, expand/collapse `<section>` (docs/design/
 * IB-49-crm-detail-timeline-design.md §5.3) — the "Details" shell every primary-entity page needs.
 * `CaseDetailScreen.tsx`'s own comment named this extraction trigger ("once a second detail page
 * needs the same shell"); IB-49 is the third and fourth primary-entity page, so it is extracted
 * here rather than inlined a third/fourth time. Deliberately minimal: tokens only, no heavyweight
 * framework — `ListView`/`RelatedList` already own the list shape, `TimelinePanel` owns the feed.
 */
export function CollapsibleSection({ title, children, defaultOpen = true }: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  const contentId = useId()

  return (
    <section
      style={{
        background: 'var(--iw-surface)',
        border: '1px solid var(--iw-border)',
        borderRadius: 'var(--iw-radius-lg)',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={contentId}
        className="iw-btn iw-btn--ghost"
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--iw-space-3) var(--iw-space-4)',
          borderRadius: 'var(--iw-radius-lg)',
          fontWeight: 'var(--iw-weight-medium)' as never,
        }}
      >
        {title}
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} />
      </button>
      {open ? (
        <div id={contentId} style={{ padding: '0 var(--iw-space-4) var(--iw-space-4)' }}>
          {children}
        </div>
      ) : null}
    </section>
  )
}
